import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  useFetchOrdersData,
  useFetchOrderTemplate,
  useOrderBulkDocMutation,
  useFetchFmOrderTemplate,
  useFmOrderBulkDocMutation,
} from "../../../apis/orderApis";
import {
  styleAndProtectDummyRow,
  cleanUploadedExcelFile,
  validateOrderBulkUploadFile,
  BulkUploadValidationError,
} from "../../../utilities/bulkUploadHelper";
import toast from "react-hot-toast";

import CustomOrderImportWizard from "../../../components/CustomOrderImport/CustomOrderImportWizard";

type OrderBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpene?: () => void;
  setOrderBulkResponse: React.Dispatch<React.SetStateAction<any>>;
  isSidebar?: boolean;
};

export default function OrderBulkUploadModals({
  onClose,
  onResponseOpene,
  setOrderBulkResponse,
  isSidebar,
}: OrderBulkUploadModalsProps) {
  const [file, setFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("All");
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [orderType, setOrderType] = useState<"LM" | "FM" | "CUSTOM">("LM");
  const [validationErrors, setValidationErrors] = useState<BulkUploadValidationError[]>([]);
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const { mutate: uploadLMDocMutate, isPending: isLMBulkUploading } =
    useOrderBulkDocMutation();
  const { mutate: uploadFMDocMutate, isPending: isFMBulkUploading } =
    useFmOrderBulkDocMutation();

  const isBulkUploading = (orderType === "LM" ? isLMBulkUploading : isFMBulkUploading) || isValidating;

  const { data: templateData, refetch: fetchTemplates } =
    useFetchOrderTemplate();
  const { data: fmTemplateData, refetch: fetchFmTemplates } =
    useFetchFmOrderTemplate();

  const {
    refetch,
  } = useFetchOrdersData(
    pageNo,
    searchFor,
    limit
  );

  const handleFileChange = async (selectedFile: File | null) => {
    setFile(selectedFile);
    setValidationErrors([]);
    if (!selectedFile || orderType === "CUSTOM") return;

    setIsValidating(true);
    try {
      const validationRes = await validateOrderBulkUploadFile(selectedFile, orderType as "LM" | "FM");
      if (!validationRes.isValid) {
        setValidationErrors(validationRes.errors);
        toast.error(`Found ${validationRes.errors.length} validation issue(s) in sheet. Please review below.`, { duration: 4000 });
      } else {
        toast.success(`Sheet validated successfully (${validationRes.dataRowCount} order rows ready)`);
      }
    } catch (err: any) {
      console.error("Validation error:", err);
    } finally {
      setIsValidating(false);
    }
  };

  const handleUploadDoc = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    if (orderType !== "CUSTOM") {
      setIsValidating(true);
      const validationRes = await validateOrderBulkUploadFile(file, orderType as "LM" | "FM");
      setIsValidating(false);

      if (!validationRes.isValid) {
        setValidationErrors(validationRes.errors);
        toast.error("Validation failed! Please fix sheet errors before submitting.");
        return;
      }
    }

    const targetTemplateObj =
      orderType === "LM" ? templateData?.[0] : fmTemplateData?.[0];
    const cleanedFile = await cleanUploadedExcelFile(file, targetTemplateObj);

    const uploadMutate = orderType === "LM" ? uploadLMDocMutate : uploadFMDocMutate;
    uploadMutate(
      { orders: cleanedFile },
      {
        onSuccess: (data: any) => {
          setOrderBulkResponse(data.data);
          onClose();
          refetch();
          toast.success("Bulk uploading is in progress. You will get notified once completed.", { duration: 5000 });
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      }
    );
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    if (orderType === "LM") {
      const worksheet = workbook.addWorksheet("Order Template");

      // Row 1: Info
      const infoRow = worksheet.addRow([
        "This template is for importing order data. Fields in red are mandatory; others are optional. Please do not modify the first 4 rows (Row 4 is sample/dummy data).",
      ]);
      worksheet.mergeCells(`A1:T1`);
      infoRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E2EFD9" },
        };
        cell.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "212121" },
        };
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });

      infoRow.height = 25;

      // Row 2: Validation row
      const validationRow = worksheet.addRow([
        "Client Id is required",
        "Reference Number is required",
        "Full name is required",
        "Receiver Phone (10–12 digits) is required",
        "Receiver Email is Optional",
        "Receiver Country.",
        "Receiver City  is required",
        "Receiver District / Area", 
        "Receiver Address is required",
        "Receiver National Address  is required",
        "Shipment Cost is Optional", 
        "Payment Type (CC, COD)",
        "Cod Amount is required", 
        "Number of Pieces is required", 
        "Weight is required", 
        "Service is required",  
        "Shipment Type (PARCEL)",  
        "Description is Optional", 
        "Latitude is Optional" ,
        "Longitude is Optional"
      ]);
      validationRow.eachCell((cell) => {
        let fillColor = "FFCCCC";

        const optionalCols = [5, 11, 18, 19, 20];
        if (optionalCols.includes(Number(cell.col))) {
          fillColor = "FFEB9C";
        }
      
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };

        cell.font = { color: { argb: "212121" } };

        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      validationRow.height = 60;

      // Row 3: Headings
      const headings = Object.keys(templateData?.[0] || {});
      const headingsRow = worksheet.addRow(headings);

      headingsRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "217447" },
        };
        cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      headingsRow.height = 25;

      // Row 4: Dummy Data Row
      let dummyRow: ExcelJS.Row | undefined;
      if (templateData && templateData.length > 0 && templateData[0]) {
        const dummyValues = Object.values(templateData[0]);
        dummyRow = worksheet.addRow(dummyValues);
        dummyRow.eachCell((cell: any) => {
          cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });
        dummyRow.height = 20;
      }

      worksheet.columns = headings.map(() => ({ width: 25 }));
      await styleAndProtectDummyRow(worksheet, dummyRow);
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "order-template.xlsx");
      toast.success("LM Orders template downloaded successfully");
    } else {
      const worksheet = workbook.addWorksheet("FM Orders Template");

      // Row 1: Info
      const infoRow = worksheet.addRow([
        "FM Orders Template — One product per row. For multiple products in the same order, repeat the same Reference No & Client ID on separate rows with different Product IDs. Do NOT modify the first 4 rows (Row 4 is sample/dummy data).",
      ]);
      worksheet.mergeCells(`A1:S1`);
      infoRow.eachCell((cell: any) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E2EFD9" },
        };
        cell.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "212121" },
        };
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      infoRow.height = 30;

      // Row 2: Validation Rules
      const validationRow = worksheet.addRow([
        "Client ID is required",
        "Reference No is required",
        "Receiver Name is required",
        "Receiver Phone (9-12 digits) is required",
        "Receiver Email (optional)",
        "Receiver Country is required",
        "Receiver City (optional if National Address provided)",
        "Receiver District / Area (optional)",
        "Receiver Address (optional if National Address provided)",
        "National Address (optional if City+Address provided)",
        "Shipment Cost (optional)",
        "Payment Type (CC, COD)",
        "COD Amount (0 for CC, required if COD)",
        "No of Pieces is required (numeric ≥1)",
        "Service (optional)",
        "Description (optional)",
        "Latitude (optional)",
        "Longitude (optional)",
        "Product ID is required (SKU) — one per row",
        "Quantity is required (numeric ≥1)",
      ]);
      validationRow.eachCell((cell: any) => {
        let fillColor = "FFCCCC";

        const optionalCols = [5, 7, 8, 9, 10, 11, 13, 15, 16, 17, 18];
        if (optionalCols.includes(Number(cell.col))) {
          fillColor = "FFEB9C";
        }

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };

        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };

        cell.font = { color: { argb: "212121" }, size: 9 };

        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      validationRow.height = 80;

      // Row 3: Column Headers
      const headings = [
          "client_id",
          "reference_no",
          "receiver_name",
          "receiver_mobile",
          "receiver_email",
          "receiver_country",
          "receiver_city",
          "receiver_district",
          "receiver_address",
          "receiver_national_address",
          "shipment_cost",
          "payment_type",
          "cod_amount",
          "no_of_pieces",
          "service",
          "description",
          "latitude",
          "longitude",
          "product_id",
          "quantity",
        ];

      const headersRow = worksheet.addRow(headings);

      headersRow.eachCell((cell: any) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "217447" },
        };
        cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      headersRow.height = 25;

      // Row 4: Dummy Data Row
      let dummyRow: ExcelJS.Row | undefined;
      if (fmTemplateData && fmTemplateData.length > 0 && fmTemplateData[0]) {
        const dummyValues = Object.values(fmTemplateData[0]);
        dummyRow = worksheet.addRow(dummyValues);
        dummyRow.eachCell((cell: any) => {
          cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });
        dummyRow.height = 20;
      }

      worksheet.columns = Array(headings.length).fill({ width: 22 });
      await styleAndProtectDummyRow(worksheet, dummyRow);
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "fm-orders-template.xlsx");
      toast.success("FM Orders template downloaded successfully");
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchFmTemplates();
  }, []);

  const content = (
    <div className={`flex flex-col h-full bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] ${isSidebar ? "rounded-3xl border border-gray-200 shadow-sm overflow-hidden" : ""}`}>
      {/* Header */}
      <div className="flex items-center flex-shrink-0">
        <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px]">
          <h2 className="text-2xl font-bold">Bulk Upload {orderType} Orders</h2>
        </div>
        <div className="relative">
          <button
            onClick={onClose}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="w-full py-4 px-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex-1 overflow-y-auto min-h-0">
        <div className="space-y-4">
          
          {/* Selection for LM vs FM vs CUSTOM */}
          <div className="flex gap-4 p-2 bg-[#FCFDF8] border border-[#D2D2D2] rounded-md flex-wrap items-center">
            <span className="text-sm font-semibold text-[#212121] mr-2">Order Type:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="LM"
                checked={orderType === "LM"}
                onChange={() => {
                  setOrderType("LM");
                  setFile(null);
                  setValidationErrors([]);
                }}
                className="accent-[#FBD037]"
              />
              <span className="text-sm font-medium text-[#212121]">LM Orders</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="FM"
                checked={orderType === "FM"}
                onChange={() => {
                  setOrderType("FM");
                  setFile(null);
                  setValidationErrors([]);
                }}
                className="accent-[#FBD037]"
              />
              <span className="text-sm font-medium text-[#212121]">FM Orders</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="CUSTOM"
                checked={orderType === "CUSTOM"}
                onChange={() => {
                  setOrderType("CUSTOM");
                  setFile(null);
                  setValidationErrors([]);
                }}
                className="accent-[#FBD037]"
              />
              <span className="text-sm font-medium text-[#212121] px-2 py-0.5 rounded font-bold">
                Raw Sheet
              </span>
            </label>
          </div>

          {orderType === "CUSTOM" ? (
            <CustomOrderImportWizard
              onClose={onClose}
              onSuccess={() => refetch()}
            />
          ) : (
            <>

          <FileUploader
            name="photo"
            extraParentClasses="col-span-3"
            img="/img/upload-img.png"
            title={`Upload ${orderType} Excel/CSV File`}
            message="Upload an Excel or CSV file (Max size: 2 MB)." 
            classNameMain="bg-transparent border-none flex items-center gap-3"
            fileVal={file}
            setFileVal={(selectedFile: any) => {
              handleFileChange(selectedFile);
            }}
            showPreview={true}
            required
          />

          {file && validationErrors.length === 0 && !isValidating && (
            <p className="text-sm text-green-700 bg-green-50 p-2.5 rounded-md border border-green-200 font-medium flex items-center gap-2">
              <span>✓</span> File ready: <strong>{file.name}</strong> (Validation passed cleanly)
            </p>
          )}

          {isValidating && (
            <p className="text-sm text-blue-600 bg-blue-50 p-2.5 rounded-md border border-blue-200 font-medium animate-pulse">
              🔍 Validating sheet fields & rules... Please wait.
            </p>
          )}

          {/* Validation Errors Box */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-red-800 uppercase tracking-wider flex items-center gap-1">
                  <span>⚠️</span> {validationErrors.length} Sheet Error(s) Found — Fix before uploading:
                </span>
                <button
                  type="button"
                  onClick={() => setValidationErrors([])}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Dismiss
                </button>
              </div>
              <ul className="space-y-1.5 text-xs text-red-700 divide-y divide-red-200/60">
                {validationErrors.map((err, idx) => (
                  <li key={idx} className="pt-1 flex items-start gap-2">
                    <span className="font-bold bg-red-200 text-red-900 px-1.5 py-0.5 rounded shrink-0">
                      {err.row > 0 ? `Row ${err.row}` : "File"}
                    </span>
                    <span>
                      <strong className="capitalize">{err.field.replace(/_/g, " ")}</strong>: {err.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
            <li>Download the Excel/CSV template before uploading.</li>
            <li>Do not change column headers.</li>
            {orderType === "FM" ? (
              <>
                <li>Include product details (Product ID & Quantity) for each order.</li>
                <li>Weight will be calculated from products - do not include weight column.</li>
                <li>Orders with missing or unstocked SKUs will automatically be placed into <strong>Pending Stock (PENDING)</strong> status.</li>
              </>
            ) : (
              <li>Mandatory fields: Client ID, Receiver, Reference, Weight, etc.</li>
            )}
          </ul>

          <div className="flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2]">
            <p className="text-xs text-[#212121] font-medium">
              <b>Note</b> : Please upload the file in the same format as the
              downloaded file.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="bg-[#000000] text-white flex items-center space-x-1 p-2 rounded-md text-sm shrink-0"
            >
              <img src="/img/csvxl.svg" alt="" />
              <span>Download</span>
            </button>
          </div>
            </>
          )}
        </div>
      </div>

      {orderType !== "CUSTOM" && (
        <div className="border-t  border-[#D8D8D8]/20 px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px] flex-shrink-0">
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading || !file}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center rounded-full p-2 px-6 space-x-1 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isBulkUploading ? "Uploading..." : "Submit"}
          </button>
        </div>
      )}
    </div>
  );

  if (isSidebar) {
    return content;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className={`rounded-2xl w-full ${orderType === "CUSTOM" ? "max-w-[760px]" : "max-w-[500px]"} h-[calc(100vh-2rem)] flex flex-col relative bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-right duration-300 transition-all`}>
        {content}
      </div>
    </div>
  );
}
